/**
 * 학습지원 소프트웨어 선정 기준 가이드 - SEO 롱테일 페이지
 * 
 * 타겟 키워드: 학습지원 소프트웨어, 학습지원 소프트웨어 기준, 학습지원 소프트웨어 선정기준
 */

import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../../components/layout/Footer';
import { NamedGrayIcon } from '../../components/icons/GrayIconBox';
import '../../styles/design-system/index.css';
import { TEACHER_COUNT_LABEL } from '../../constants/siteStats';

const LearningSoftwareGuidePage = () => {
  const navigate = useNavigate();

  const requiredCriteria = [
    {
      title: '개인정보보호 법령 준수',
      description: '개인정보보호법, 정보통신망법 등 관련 법령 준수 여부',
      icon: 'lock'
    },
    {
      title: '학습데이터 수집·활용 투명성',
      description: '수집되는 데이터 항목, 활용 목적, 보관 기간 명시',
      icon: 'chart'
    },
    {
      title: '보안 취약점 점검 및 조치',
      description: '정기적 보안 점검, 취약점 발견 시 신속 조치 체계',
      icon: 'shield'
    },
    {
      title: '접근성 보장',
      description: '장애학생, 다문화가정 학생 등 모든 학생의 접근성 보장',
      icon: 'accessibility'
    },
    {
      title: '윤리적 AI 활용',
      description: 'AI 기반 서비스의 경우 편향성 검토, 윤리적 활용 기준 준수',
      icon: 'spark'
    }
  ];

  const optionalCriteria = [
    { title: '교육과정 연계성', description: '국가 교육과정과의 연계 정도' },
    { title: '학습 효과성 검증', description: '학습 효과에 대한 실증적 검증 자료' },
    { title: '교사·학생 편의성', description: 'UI/UX 편의성, 사용자 만족도' },
    { title: '기술 지원 체계', description: '고객지원, 장애 대응, 업데이트 체계' }
  ];

  return (
    <>
      <Helmet>
        <title>학습지원 소프트웨어 선정 기준 | 2026 교육부 필수·선택 기준 완전 정리</title>
        <meta name="description" content="2026년 교육부 학습지원 소프트웨어 선정 기준 완전 정리. 필수 기준 5개, 선택 기준 4개 체크리스트. 학교장·정보부장 필독. 회의자료 템플릿 무료 제공." />
        <meta name="keywords" content="학습지원 소프트웨어, 학습지원 소프트웨어 기준, 학습지원 소프트웨어 선정기준, 학습지원 소프트웨어 선정 기준, 교육부 학습지원, 에듀테크 기준, 초중등교육법 제29조의2, 학교 소프트웨어 도입" />
        <link rel="canonical" href="https://whatquiz.co.kr/guide/learning-software" />
        
        <meta name="robots" content="index, follow" />
        
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://whatquiz.co.kr/guide/learning-software" />
        <meta property="og:title" content="학습지원 소프트웨어 선정 기준 | 2026 교육부 기준 완전 정리" />
        <meta property="og:description" content="교육부 학습지원 소프트웨어 필수·선택 기준 체크리스트. 회의자료 템플릿 무료." />
        <meta property="og:image" content="https://whatquiz.co.kr/og-image-learning-software.png" />
        <meta property="og:locale" content="ko_KR" />
        
        {/* 구조화된 데이터 - FAQ */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "학습지원 소프트웨어란 무엇인가요?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "학습지원 소프트웨어는 학생의 학습을 지원하기 위해 학교에서 도입하는 에듀테크 소프트웨어를 말합니다. 초중등교육법 제29조의2에 따라 학교장이 선정 기준을 확인하고 도입해야 합니다."
                }
              },
              {
                "@type": "Question",
                "name": "학습지원 소프트웨어 선정 기준은 누가 확인하나요?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "학교장 또는 정보담당 부장교사가 확인합니다. 학교운영위원회 심의를 거쳐야 하며, 선정위원회를 구성하여 기준 충족 여부를 검토합니다."
                }
              },
              {
                "@type": "Question",
                "name": "필수 기준 5개는 무엇인가요?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "1) 개인정보보호 법령 준수, 2) 학습데이터 수집·활용 투명성, 3) 보안 취약점 점검 및 조치, 4) 접근성 보장, 5) 윤리적 AI 활용입니다."
                }
              }
            ]
          })}
        </script>
        
        {/* 구조화된 데이터 - Article */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "학습지원 소프트웨어 선정 기준 2026 완전 정리",
            "description": "교육부 학습지원 소프트웨어 필수 기준 5개, 선택 기준 4개 체크리스트",
            "author": { "@type": "Organization", "name": "왓퀴즈" },
            "publisher": { "@type": "Organization", "name": "왓퀴즈", "logo": "https://whatquiz.co.kr/images/logo512.png" },
            "datePublished": "2026-02-05",
            "dateModified": "2026-02-05"
          })}
        </script>
      </Helmet>

      <div style={{ 
        fontFamily: 'var(--whatquiz-font-family)',
        backgroundColor: '#fff',
        minHeight: '100vh'
      }}>
        {/* 히어로 섹션 */}
        <section style={{
          padding: '80px 20px 60px',
          textAlign: 'center',
          background: 'linear-gradient(180deg, #fef3c7 0%, #fff 100%)'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <span style={{
              display: 'inline-block',
              padding: '6px 16px',
              backgroundColor: '#fef08a',
              color: '#a16207',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '16px'
            }}>
              2025년 12월 교육부 발표
            </span>
            
            <h1 style={{
              fontSize: 'clamp(26px, 5vw, 40px)',
              fontWeight: '800',
              color: '#111',
              lineHeight: '1.3',
              marginBottom: '20px'
            }}>
              학습지원 소프트웨어 선정 기준<br />
              <span style={{ color: '#d97706' }}>완전 정리</span>
            </h1>
            
            <p style={{
              fontSize: '17px',
              color: '#64748b',
              lineHeight: '1.7',
              marginBottom: '24px'
            }}>
              학교장·정보부장이 꼭 확인해야 할<br />
              <strong>필수 기준 5개 + 선택 기준 4개</strong> 체크리스트
            </p>

            <div style={{
              display: 'inline-flex',
              gap: '12px',
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}>
              <Link to="/guide/edzip" style={{
                padding: '10px 20px',
                fontSize: '14px',
                color: '#3182f6',
                backgroundColor: '#eff6ff',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '500'
              }}>
                에듀집 가이드 보기 →
              </Link>
            </div>
          </div>
        </section>

        {/* 배경 설명 */}
        <section style={{
          padding: '60px 20px',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontSize: '22px',
            fontWeight: '700',
            color: '#111',
            marginBottom: '20px'
          }}>
            학습지원 소프트웨어란?
          </h2>
          
          <div style={{
            padding: '24px',
            backgroundColor: '#fffbeb',
            borderRadius: '16px',
            borderLeft: '4px solid #f59e0b',
            marginBottom: '24px'
          }}>
            <p style={{
              fontSize: '15px',
              color: '#374151',
              lineHeight: '1.8',
              margin: 0
            }}>
              <strong>학습지원 소프트웨어</strong>는 학생의 학습을 지원하기 위해 학교에서 도입하는 
              에듀테크 소프트웨어를 말합니다.<br /><br />
              
              <strong>초중등교육법 제29조의2</strong> 개정으로, 2026년부터 학교장이 에듀테크를 도입할 때 
              <strong style={{ color: '#d97706' }}> 교육부가 제시한 선정 기준을 반드시 확인</strong>해야 합니다.
            </p>
          </div>

          <div style={{
            padding: '20px',
            backgroundColor: '#f8fafc',
            borderRadius: '12px',
            fontSize: '14px',
            color: '#64748b',
            lineHeight: '1.7'
          }}>
            <strong style={{ color: '#374151' }}>적용 대상:</strong> 모든 초·중·고등학교<br />
            <strong style={{ color: '#374151' }}>시행 시기:</strong> 2026년 3월부터 적용<br />
            <strong style={{ color: '#374151' }}>근거:</strong> 초중등교육법 제29조의2, 교육부 고시 제2025-XX호
          </div>
        </section>

        {/* 필수 기준 */}
        <section style={{
          padding: '60px 20px',
          backgroundColor: '#fef2f2'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: '22px',
              fontWeight: '700',
              color: '#111',
              marginBottom: '8px'
            }}>
              필수 기준 5개
            </h2>
            <p style={{
              fontSize: '15px',
              color: '#dc2626',
              marginBottom: '24px',
              fontWeight: '500'
            }}>
              5개 모두 충족해야 도입 가능
            </p>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              {requiredCriteria.map((item, idx) => (
                <div key={idx} style={{
                  padding: '20px',
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                  border: '1px solid #fecaca',
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'flex-start'
                }}>
                  <NamedGrayIcon name={item.icon} boxSize={40} />
                  <div>
                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#111',
                      marginBottom: '6px'
                    }}>
                      {idx + 1}. {item.title}
                    </h4>
                    <p style={{
                      fontSize: '14px',
                      color: '#64748b',
                      margin: 0,
                      lineHeight: '1.6'
                    }}>
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 선택 기준 */}
        <section style={{
          padding: '60px 20px',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontSize: '22px',
            fontWeight: '700',
            color: '#111',
            marginBottom: '8px'
          }}>
            선택 기준 4개
          </h2>
          <p style={{
            fontSize: '15px',
            color: '#64748b',
            marginBottom: '24px'
          }}>
            권장 사항으로, 학교 상황에 따라 검토
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '16px'
          }}>
            {optionalCriteria.map((item, idx) => (
              <div key={idx} style={{
                padding: '20px',
                backgroundColor: '#f8fafc',
                borderRadius: '12px'
              }}>
                <h4 style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  {item.title}
                </h4>
                <p style={{
                  fontSize: '13px',
                  color: '#64748b',
                  margin: 0,
                  lineHeight: '1.5'
                }}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* 실무 가이드 */}
        <section style={{
          padding: '60px 20px',
          backgroundColor: '#f0f9ff'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: '22px',
              fontWeight: '700',
              color: '#111',
              marginBottom: '24px'
            }}>
              학교에서 해야 할 일
            </h2>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              {[
                { step: '1', title: '선정위원회 구성', desc: '정보부장, 담당교사, 학부모 등으로 구성' },
                { step: '2', title: '기준 충족 여부 검토', desc: '필수 기준 5개 전체 확인, 에듀집에서 등록 여부 확인' },
                { step: '3', title: '회의 및 심의', desc: '선정위원회 회의록 작성, 학교운영위원회 심의' },
                { step: '4', title: '결재 및 도입', desc: '학교장 결재 후 도입, 연간 재검토' }
              ].map((item) => (
                <div key={item.step} style={{
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'flex-start',
                  padding: '20px',
                  backgroundColor: '#fff',
                  borderRadius: '12px'
                }}>
                  <span style={{
                    width: '32px',
                    height: '32px',
                    backgroundColor: '#3182f6',
                    color: '#fff',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: '700',
                    flexShrink: 0
                  }}>{item.step}</span>
                  <div>
                    <h4 style={{
                      fontSize: '15px',
                      fontWeight: '600',
                      color: '#111',
                      marginBottom: '4px'
                    }}>{item.title}</h4>
                    <p style={{
                      fontSize: '14px',
                      color: '#64748b',
                      margin: 0
                    }}>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA 섹션 */}
        <section style={{
          padding: '80px 20px',
          textAlign: 'center',
          background: 'linear-gradient(180deg, #fff 0%, #eff6ff 100%)'
        }}>
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: '26px',
              fontWeight: '700',
              color: '#111',
              marginBottom: '16px'
            }}>
              회의자료·보고서 작성,<br />
              <span style={{ color: '#3182f6' }}>AI가 대신해드립니다</span>
            </h2>
            
            <p style={{
              fontSize: '16px',
              color: '#64748b',
              lineHeight: '1.7',
              marginBottom: '32px'
            }}>
              학습지원 소프트웨어 선정위원회 회의록<br />
              학교운영위원회 심의자료까지<br />
              <strong>왓퀴즈 AI가 3분 만에 작성</strong>해드립니다.
            </p>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              alignItems: 'center'
            }}>
              <button
                onClick={() => navigate('/chat')}
                style={{
                  padding: '16px 40px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#fff',
                  backgroundColor: '#3182f6',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer'
                }}
              >
                회의자료 자동 생성 체험하기
              </button>
              
              <button
                onClick={() => navigate('/create-saenggibu')}
                style={{
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#3182f6',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                무료로 생기부 써보기 →
              </button>
            </div>

            <p style={{
              marginTop: '24px',
              fontSize: '13px',
              color: '#94a3b8'
            }}>
              현직 교사 {TEACHER_COUNT_LABEL} 이상 사용 중
            </p>
          </div>
        </section>

        {/* 관련 가이드 */}
        <section style={{
          padding: '40px 20px',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111',
            marginBottom: '16px'
          }}>
            관련 가이드
          </h3>
          
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <Link to="/guide/edzip" style={{
              padding: '12px 20px',
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              color: '#334155',
              textDecoration: 'none',
              fontSize: '14px'
            }}>
              에듀집 완전정리
            </Link>
            <Link to="/saenggibu-inspector" style={{
              padding: '12px 20px',
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              color: '#334155',
              textDecoration: 'none',
              fontSize: '14px'
            }}>
              생기부 기재요령 검사기
            </Link>
            <Link to="/create-saenggibu" style={{
              padding: '12px 20px',
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              color: '#334155',
              textDecoration: 'none',
              fontSize: '14px'
            }}>
              AI 생기부 자동 작성
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default LearningSoftwareGuidePage;
